import { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import WatchlistTable from "@/components/WatchlistTable";
import AlertsPanel from "@/components/AlertsPanel";
import MarketOverview from "@/components/MarketOverview";
import AddTickerDialog from "@/components/AddTickerDialog";
import { Plus, ArrowClockwise, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Dashboard() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notifOn, setNotifOn] = useState(
    typeof window !== "undefined" && Notification?.permission === "granted"
  );

  const { data: rows, isLoading, mutate: mutateRows } = useSWR(
    "watchlist-live",
    () => api.liveWatchlist(),
    { refreshInterval: 30000, revalidateOnFocus: false }
  );

  const refreshAll = async () => {
    await mutateRows();
    await mutate("alerts");
    await mutate("watchlist-live-tape");
    toast.success("Refreshed");
  };

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Browser notifications not supported");
      return;
    }
    const perm = await Notification.requestPermission();
    setNotifOn(perm === "granted");
    if (perm === "granted") toast.success("Browser alerts enabled");
    else toast.error("Notifications blocked");
  };

  return (
    <div className="px-8 py-6" data-testid="dashboard-root">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">01 // MISSION CONTROL</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">
            Watchlist
          </h1>
          <p className="text-zinc-500 text-sm mt-1">
            Live prices, technical signals and AI reads on the tickers you follow.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="dashboard-notif-btn"
            onClick={enableNotifications}
            className={`h-10 px-3 border text-xs uppercase font-bold tracking-widest flex items-center gap-2 transition-colors ${
              notifOn
                ? "bg-[#FFD600] text-black border-[#FFD600] hover:bg-white"
                : "bg-transparent text-zinc-400 border-zinc-800 hover:border-white hover:text-white"
            }`}
          >
            <BellRinging size={14} weight="bold" />
            {notifOn ? "ALERTS ON" : "ENABLE ALERTS"}
          </button>
          <button
            data-testid="dashboard-refresh-btn"
            onClick={refreshAll}
            className="h-10 px-3 border border-zinc-800 hover:border-white text-xs uppercase font-bold tracking-widest text-zinc-400 hover:text-white flex items-center gap-2 transition-colors"
          >
            <ArrowClockwise size={14} weight="bold" />
            REFRESH
          </button>
          <button
            data-testid="dashboard-add-ticker-btn"
            onClick={() => setDialogOpen(true)}
            className="h-10 px-4 bg-white text-black hover:bg-[#00FF66] text-xs uppercase font-bold tracking-widest flex items-center gap-2 transition-colors"
          >
            <Plus size={14} weight="bold" />
            ADD TICKER
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-0 border border-zinc-800">
        <div className="border-r-0 xl:border-r border-zinc-800">
          <WatchlistTable
            rows={rows || []}
            isLoading={isLoading}
            onChange={mutateRows}
            notifEnabled={notifOn}
          />
        </div>
        <div className="border-t xl:border-t-0 border-zinc-800">
          <AlertsPanel />
        </div>
      </div>

      {/* Market overview */}
      <div className="mt-8">
        <div className="overline mb-3">02 // GLOBAL PULSE</div>
        <MarketOverview />
      </div>

      <AddTickerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onAdded={mutateRows}
      />
    </div>
  );
}
